import React, { useState, useEffect } from 'react';
import { Box, Typography, Popover, IconButton, Button, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import EventIcon from '@material-ui/icons/Event';
import ClearIcon from '@material-ui/icons/Close';
import moment from 'moment';
import 'moment/locale/pt';

moment.locale('pt');

const WEEKDAYS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const useStyles = makeStyles(() => ({
  trigger: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    border: '1px solid rgba(0,0,0,0.23)',
    borderRadius: 8,
    cursor: 'pointer',
    background: '#ffffff',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxSizing: 'border-box',
    '&:hover': { borderColor: '#3b82f6', boxShadow: '0 0 0 1px rgba(59,130,246,0.15)' },
  },
  triggerActive: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 2px rgba(59,130,246,0.2)',
  },
  iconBox: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  paper: {
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
    border: '1px solid rgba(0,0,0,0.07)',
    width: 284,
  },
  calHeader: {
    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    padding: '16px 18px 14px',
    color: '#fff',
  },
  monthNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navBtn: { color: 'rgba(255,255,255,0.85)', padding: 4, '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } },
  dayLabels: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    padding: '10px 12px 4px',
    background: '#fafbff',
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 2,
    padding: '2px 12px 10px',
    background: '#fafbff',
  },
  divider: {
    height: 1,
    background: 'rgba(0,0,0,0.07)',
    margin: '0 12px',
  },
  timeSection: {
    padding: '10px 16px 6px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
  },
  timeInput: {
    width: 46,
    textAlign: 'center',
    border: '1px solid rgba(0,0,0,0.18)',
    borderRadius: 8,
    padding: '6px 4px',
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#0f172a',
    outline: 'none',
    fontFamily: 'Roboto, sans-serif',
    appearance: 'textfield',
    MozAppearance: 'textfield',
    '&::-webkit-inner-spin-button': { display: 'none' },
    '&:focus': { borderColor: '#3b82f6', boxShadow: '0 0 0 2px rgba(59,130,246,0.2)' },
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  confirmBtn: {
    margin: '8px 16px 14px',
    width: 'calc(100% - 32px)',
    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    color: '#fff',
    fontWeight: 700,
    borderRadius: 10,
    padding: '8px 0',
    fontSize: '0.8rem',
    letterSpacing: '0.03em',
    boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #60a5fa 0%, #818cf8 100%)',
      boxShadow: '0 6px 18px rgba(59,130,246,0.4)',
    },
  },
}));

function buildCalendarDays(viewMonth) {
  const start = viewMonth.clone().startOf('month');
  const daysInMonth = viewMonth.daysInMonth();
  const startDow = start.isoWeekday(); // 1=Mon … 7=Sun
  const cells = [];
  for (let i = 1; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function pad(n) { return String(n).padStart(2, '0'); }

export default function DateTimePicker({ value, onChange, label, compact, tooltip }) {
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const [viewMonth, setViewMonth] = useState(() =>
    value ? moment(value) : moment()
  );
  // Pre-select TODAY when no value is provided so the Confirm button is
  // enabled out of the box. Operators almost never use the calendar to pick a
  // day — they just type the hour and hit confirm. Leaving selDay=null in that
  // case made the button greyed-out and "Confirmar não fazia nada".
  const [selDay, setSelDay] = useState(() =>
    value ? moment(value) : moment().startOf('day')
  );
  const [hour, setHour] = useState(() =>
    value ? moment(value).format('HH') : moment().format('HH')
  );
  const [minute, setMinute] = useState(() =>
    value ? moment(value).format('mm') : '00'
  );

  useEffect(() => {
    if (value) {
      const m = moment(value);
      setViewMonth(m.clone());
      setSelDay(m.clone());
      setHour(m.format('HH'));
      setMinute(m.format('mm'));
    } else {
      // Same default as initial state — keep Confirm clickable.
      const today = moment().startOf('day');
      setSelDay(today);
      setHour(moment().format('HH'));
      setMinute('00');
      setViewMonth(today.clone());
    }
  }, [value]);

  const open = Boolean(anchor);
  const today = moment().startOf('day');
  const cells = buildCalendarDays(viewMonth);

  const displayValue = selDay
    ? `${selDay.format('ddd, DD MMM YYYY')} · ${pad(hour)}:${pad(minute)}`
    : null;

  function handleDayClick(day) {
    const d = viewMonth.clone().date(day);
    setSelDay(d);
  }

  function handleConfirm() {
    if (!selDay) return;
    const h = Math.min(23, Math.max(0, parseInt(hour) || 0));
    const m = Math.min(59, Math.max(0, parseInt(minute) || 0));
    const result = selDay.clone().hour(h).minute(m).second(0);
    onChange(result.format('YYYY-MM-DDTHH:mm'));
    setAnchor(null);
  }

  function handleHourChange(e) {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (v === '' || (parseInt(v) >= 0 && parseInt(v) <= 23)) setHour(v);
  }

  function handleMinuteChange(e) {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (v === '' || (parseInt(v) >= 0 && parseInt(v) <= 59)) setMinute(v);
  }

  function dayCellStyle(day) {
    const base = {
      width: 34, height: 34, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', fontSize: '0.77rem', fontFamily: 'Roboto, sans-serif',
      transition: 'all 0.15s', border: 'none', userSelect: 'none',
    };
    const isSelected = selDay && selDay.date() === day && selDay.month() === viewMonth.month() && selDay.year() === viewMonth.year();
    const isToday = viewMonth.clone().date(day).isSame(today, 'day');
    if (isSelected) return { ...base, background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff', fontWeight: 700, boxShadow: '0 2px 8px rgba(59,130,246,0.4)' };
    if (isToday) return { ...base, border: '2px solid #3b82f6', color: '#3b82f6', fontWeight: 700, background: 'transparent' };
    return { ...base, color: '#334155', background: 'transparent' };
  }

  function handleClear(e) {
    e.stopPropagation();
    onChange('');
  }

  const triggerContent = (
    <Box
      className={`${classes.trigger} ${open ? classes.triggerActive : ''}`}
      onClick={e => setAnchor(e.currentTarget)}
      style={compact ? { padding: '3px 6px', gap: 4 } : undefined}
    >
      <Box className={classes.iconBox} style={compact ? { width: 18, height: 18, borderRadius: 4 } : undefined}>
        <EventIcon style={{ color: '#fff', fontSize: compact ? 10 : 15 }} />
      </Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Typography style={{ fontSize: compact ? '0.5rem' : '0.6rem', color: '#94a3b8', lineHeight: 1, marginBottom: compact ? 0 : 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
        <Typography style={{ fontSize: compact ? '0.58rem' : '0.8rem', color: displayValue ? '#0f172a' : '#94a3b8', fontWeight: displayValue ? 500 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {displayValue || (compact ? 'Selecionar...' : 'Selecionar data e hora')}
        </Typography>
      </Box>
      {value && (
        <IconButton size="small" onClick={handleClear} style={{ padding: 2, color: '#94a3b8' }}>
          <ClearIcon style={{ fontSize: compact ? 12 : 14 }} />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      {tooltip ? <Tooltip title={tooltip} placement="top">{triggerContent}</Tooltip> : triggerContent}

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.paper }}
        style={{ marginTop: 4 }}
      >
        {/* Header */}
        <Box className={classes.calHeader}>
          <Typography style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>
            Data de Partida
          </Typography>
          <Typography style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', letterSpacing: '0.01em' }}>
            {selDay
              ? selDay.format('ddd, DD [de] MMMM YYYY')
              : 'Escolha um dia'}
          </Typography>
          {selDay && (
            <Typography style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>
              {pad(hour)}:{pad(minute)}
            </Typography>
          )}
        </Box>

        {/* Month navigation */}
        <Box className={classes.dayLabels}>
          <Box style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <IconButton size="small" className={classes.navBtn} onClick={() => setViewMonth(v => v.clone().subtract(1, 'month'))}>
              <ChevronLeftIcon style={{ fontSize: 18 }} />
            </IconButton>
            <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
              {viewMonth.format('MMMM YYYY')}
            </Typography>
            <IconButton size="small" className={classes.navBtn} style={{ color: '#64748b' }} onClick={() => setViewMonth(v => v.clone().add(1, 'month'))}>
              <ChevronRightIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          {WEEKDAYS.map(d => (
            <Typography key={d} style={{ textAlign: 'center', fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {d}
            </Typography>
          ))}
        </Box>

        {/* Days grid */}
        <Box className={classes.daysGrid}>
          {cells.map((day, idx) =>
            day === null ? (
              <Box key={`e-${idx}`} />
            ) : (
              <Box
                key={day}
                style={dayCellStyle(day)}
                onClick={() => handleDayClick(day)}
              >
                {day}
              </Box>
            )
          )}
        </Box>

        <Box className={classes.divider} />

        {/* Time picker */}
        <Box className={classes.timeSection}>
          <Typography style={{ fontSize: '0.72rem', color: '#64748b', flex: 1 }}>Hora de partida</Typography>
          <input
            className={classes.timeInput}
            type="number"
            min="0" max="23"
            value={hour}
            onChange={handleHourChange}
            onBlur={() => setHour(pad(Math.min(23, Math.max(0, parseInt(hour) || 0))))}
          />
          <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#94a3b8' }}>:</Typography>
          <input
            className={classes.timeInput}
            type="number"
            min="0" max="59"
            value={minute}
            onChange={handleMinuteChange}
            onBlur={() => setMinute(pad(Math.min(59, Math.max(0, parseInt(minute) || 0))))}
          />
        </Box>

        {/* Confirm + Clear */}
        <Box style={{ display: 'flex', gap: 8, margin: '8px 16px 14px', alignItems: 'center' }}>
          {value && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => { onChange(''); setAnchor(null); }}
              style={{ color: '#ef4444', borderColor: '#ef4444', fontSize: '0.7rem', fontWeight: 600, borderRadius: 10, padding: '6px 12px', whiteSpace: 'nowrap' }}
            >
              Limpar
            </Button>
          )}
          <Button
            fullWidth
            variant="contained"
            className={classes.confirmBtn}
            onClick={handleConfirm}
            disabled={!selDay}
            style={{ margin: 0 }}
          >
            Confirmar
          </Button>
        </Box>
      </Popover>
    </>
  );
}
